import React, { useState, useCallback, useEffect } from 'react';
import { Phone, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const CrisisHotlines = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  const hotlines = [
    {
      name: '911',
      description: 'Emergency Services',
      number: '911',
      type: 'emergency'
    },
    {
      name: '988 Suicide Crisis Helpline',
      description: 'Call or text - National',
      number: '988',
      hours: '24/7',
      type: 'emergency'
    },
    {
      name: 'NORS Canada',
      description: 'Virtual Safe Consumption - Peer Support',
      number: '1-888-688-6677',
      hours: '24/7',
      type: 'harm-reduction'
    },
    {
      name: 'Gerstein Crisis Centre',
      description: 'Toronto Mental Health Crisis',
      number: '416-929-5200',
      hours: '24/7'
    },
    {
      name: 'Ottawa Crisis Line',
      description: 'Ottawa Mental Health Crisis',
      number: '613-722-6914',
      hours: '24/7'
    },
    {
      name: 'ConnexOntario',
      description: 'Mental Health & Addictions',
      number: '1-866-531-2600',
      hours: '24/7'
    },
    {
      name: '211',
      description: 'Community Services',
      number: '211',
      hours: '24/7'
    },
    {
      name: 'Anchor',
      description: 'Ottawa Crisis & Harm Reduction',
      number: '613-238-8210',
      hours: 'Call for hours'
    }
  ];

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isExpanded && !event.target.closest('[data-testid="crisis-hotlines-widget"]')) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isExpanded) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isExpanded]);

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const closeWidget = useCallback(() => {
    setIsExpanded(false);
  }, []);

  return (
    <div className="fixed bottom-20 right-6 z-[9999]" data-testid="crisis-hotlines-widget">
      {isExpanded ? (
        <div 
          className="bg-[rgb(var(--surface))] border border-[rgb(var(--danger))]/30 rounded-2xl shadow-2xl p-5 w-80 max-h-[80vh] overflow-y-auto animate-in fade-in slide-in-from-bottom-4 duration-200"
          style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }}
        >
          <div className="flex items-center justify-between mb-4 sticky top-0 bg-[rgb(var(--surface))] pb-2">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-[rgb(var(--danger))]/20 flex items-center justify-center mr-3 animate-pulse">
                <Phone size={20} weight="duotone" className="text-[rgb(var(--danger))]" />
              </div>
              <h3 className="font-semibold text-[rgb(var(--text-primary))]">
                Crisis Hotlines
              </h3>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={closeWidget}
              data-testid="close-hotlines"
              className="text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--text-primary))] hover:bg-white/10"
            >
              <X size={20} />
            </Button>
          </div>

          <div className="space-y-3">
            {hotlines.map((hotline, index) => (
              <a
                key={index}
                href={`tel:${hotline.number.replace(/[^0-9+]/g, '')}`}
                data-testid={`hotline-${hotline.name.toLowerCase().replace(/\s+/g, '-')}`}
                className="block p-4 bg-[rgb(var(--background))] hover:bg-[rgb(var(--surface-elevated))] rounded-xl border border-white/5 hover:border-[rgb(var(--danger))]/30 transition-all group"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="font-semibold text-[rgb(var(--text-primary))] mb-1 flex items-center flex-wrap gap-1">
                      {hotline.name}
                      {hotline.type === 'emergency' && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-[rgb(var(--danger))]/20 text-[rgb(var(--danger))] rounded-full">
                          EMERGENCY
                        </span>
                      )}
                      {hotline.type === 'harm-reduction' && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-[rgb(var(--primary))]/20 text-[rgb(var(--primary))] rounded-full">
                          PEER SUPPORT
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[rgb(var(--text-secondary))] mb-2">
                      {hotline.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-base font-bold text-[rgb(var(--primary))] group-hover:text-[rgb(var(--accent))]">
                        {hotline.number}
                      </span>
                      {hotline.hours && (
                        <span className="text-xs text-[rgb(var(--text-secondary))]">
                          {hotline.hours}
                        </span>
                      )}
                    </div>
                  </div>
                  <Phone size={20} weight="duotone" className="text-[rgb(var(--primary))] ml-2 mt-1 group-hover:scale-110 transition-transform" />
                </div>
              </a>
            ))}
          </div>

          <p className="text-xs text-[rgb(var(--text-secondary))] mt-4 text-center">
            All calls are confidential
          </p>
        </div>
      ) : (
        <button
          onClick={toggleExpanded}
          data-testid="open-hotlines"
          className="group bg-[rgb(var(--danger))] hover:bg-[rgb(var(--danger))]/90 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all hover:scale-110 flex items-center space-x-2 animate-pulse hover:animate-none"
          style={{ boxShadow: '0 0 20px rgba(239, 68, 68, 0.4)' }}
          aria-label="Open Crisis Hotlines"
        >
          <Phone size={24} weight="bold" />
          <span className="font-semibold pr-2">Crisis Help</span>
        </button>
      )}
    </div>
  );
};

export default CrisisHotlines;